import { IconKey2 } from "central-icons/IconKey2";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useGetCustomDomain, useDeleteCustomDomain, useSetEnforceDomainPolicy, useListSSOConfigurations } from "@/queries/organization-queries";
import { SetupCustomDomainDialog } from "@/routes/settings/login/SetupCustomDomainDialog";
import { formatError } from "@/utils/errors";
import { useState, type FC } from "react";
import { TrackLocations } from "@/hooks/use-segment";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Alert, AlertDescription } from "@/components/podkit/alert/Alert";
import { IconTriangleExclamation } from "central-icons/IconTriangleExclamation";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { CustomDomainProvider, SSOConfiguration_ProviderType } from "gitpod-next-api/gitpod/v1/organization_pb";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";

type CustomDomainCardProps = {
    organizationId: string;
    editable?: boolean;
};

export const CustomDomainCard: FC<CustomDomainCardProps> = ({ organizationId, editable = true }) => {
    const { toast } = useToast();
    const [showSetupDialog, setShowSetupDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const { data: customDomain, isLoading } = useGetCustomDomain(organizationId);
    const { data: ssoConfigurations } = useListSSOConfigurations();
    const deleteMutation = useDeleteCustomDomain();
    const setEnforcementPolicy = useSetEnforceDomainPolicy();

    const handleEdit = () => {
        setIsEditing(true);
        setShowSetupDialog(true);
    };

    const handleSetup = () => {
        setIsEditing(false);
        setShowSetupDialog(true);
    };

    const handleDelete = async () => {
        try {
            // First delete the domain, then disable enforcement for consistency
            // If deletion succeeds but enforcement update fails, it's not critical
            // since enforcement has no effect without a registered domain
            await deleteMutation.mutateAsync({ organizationId });
            await setEnforcementPolicy.mutateAsync({ organizationId, enforced: false });
            toast({
                title: "Custom domain deleted successfully",
            });
            setShowDeleteDialog(false);
        } catch (error) {
            toast({
                title: "Failed to delete custom domain",
                description: formatError(error),
            });
        }
    };

    if (isLoading) {
        return (
            <Card className="flex w-[550px] flex-col gap-4 bg-surface-glass p-4" variant="bordered">
                <div className="flex gap-4">
                    <div className="flex w-11 items-center justify-center">
                        <IconKey2 size={24} className="text-content-primary" />
                    </div>
                    <div className="flex flex-col gap-3">
                        <p className="text-base font-normal text-content-primary">Custom domain</p>
                        <SkeletonBlock ready={false} className="h-16 w-full" />
                    </div>
                </div>
            </Card>
        );
    }

    const hasDomain = !!customDomain;
    
    // Check if there's a custom SSO configuration (providerType === CUSTOM)
    const hasCustomSSO = ssoConfigurations?.some(
        (config) => config.providerType === SSOConfiguration_ProviderType.CUSTOM
    );

    return (
        <>
            <Card className="flex w-[550px] flex-col gap-4 bg-surface-glass p-4" variant="bordered">
                <div className="flex gap-4">
                    <div className="flex w-11 items-center justify-center">
                        <IconKey2 size={24} className="text-content-primary" />
                    </div>
                    <div className="flex flex-1 flex-col gap-3">
                        <div className="flex flex-col gap-1">
                            <p className="text-base font-normal text-content-primary">Custom domain</p>
                            {!hasDomain && (
                                <span className="text-sm text-content-secondary">
                                    Set up a private, branded domain for your organization&apos;s Ona deployment.
                                </span>
                            )}
                            {hasDomain && (
                                <div className="flex flex-col gap-1">
                                    <div className="text-sm text-content-secondary">
                                        Your custom domain:{" "}
                                        <ExternalLink
                                            href={`https://${customDomain.domainName}`}
                                            className="text-sm"
                                            iconSize="sm"
                                        >
                                            {customDomain.domainName}
                                        </ExternalLink>
                                    </div>
                                    <div className="text-sm text-content-secondary">
                                        {customDomain.provider === CustomDomainProvider.GCP
                                            ? `GCP Project ID: ${customDomain.cloudAccountId || customDomain.awsAccountId}`
                                            : `AWS Account ID: ${customDomain.cloudAccountId || customDomain.awsAccountId}`}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {!hasDomain && (
                        <div className="flex items-start">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleSetup}
                                disabled={!editable}
                                data-tracking-id="setup-custom-domain"
                            >
                                Set Up
                            </Button>
                        </div>
                    )}
                    {hasDomain && (
                        <div className="flex items-start gap-4">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleEdit}
                                disabled={!editable}
                                data-tracking-id="edit-custom-domain"
                            >
                                Edit
                            </Button>
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setShowDeleteDialog(true)}
                                disabled={!editable}
                                data-tracking-id="delete-custom-domain"
                            >
                                Delete
                            </Button>
                        </div>
                    )}
                </div>

                {hasDomain && !hasCustomSSO && (
                    <Alert data-testid="sso-warning-banner">
                        <AlertDescription className="flex items-start gap-3">
                            <IconTriangleExclamation className="size-5 text-content-warning flex-shrink-0" />
                            <div>
                                <p className="font-medium mb-1">Single Sign-On required</p>
                                <p>
                                    Custom domains require Single Sign-On (SSO) to be configured. Please set up SSO in the Login Methods section below.
                                </p>
                            </div>
                        </AlertDescription>
                    </Alert>
                )}
            </Card>

            {showSetupDialog && (
                <SetupCustomDomainDialog
                    organizationId={organizationId}
                    isOpen={showSetupDialog}
                    onClose={() => {
                        setShowSetupDialog(false);
                        setIsEditing(false);
                    }}
                    isEditing={isEditing}
                    existingDomain={isEditing ? customDomain : undefined}
                />
            )}

            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent data-track-location={TrackLocations.DeleteCustomDomainModal}>
                    <DialogHeader>
                        <DialogTitle>Delete custom domain</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete the custom domain configuration for{" "}
                            <strong className="font-semibold">{customDomain?.domainName}</strong>? This action cannot be
                            undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogBody>
                        <div className="bg-surface-warning-subtle/15 border border-border-warning/30 rounded-md p-4">
                            <div className="flex">
                                <IconTriangleExclamation className="size-5 text-content-warning mr-3 flex-shrink-0" />
                                <div className="text-base text-content-orange">
                                    <p className="font-medium mb-1">This will:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Remove the custom domain configuration</li>
                                        <li>Automatically disable domain enforcement</li>
                                        <li>Require reconfiguration to use custom domain again</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </DialogBody>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" disabled={deleteMutation.isPending}>
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            loading={deleteMutation.isPending}
                            data-tracking-id="confirm-delete-custom-domain"
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
